import { AidStation, Segment, CalibrationProfile } from '../../types'
import { formatTime } from '../../lib/calculations'
import { calculateSegmentTimeline } from '../../lib/timeline'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Badge } from '../ui/badge'

interface SharedAidStationsViewProps {
  aidStations: AidStation[]
  segments: Segment[]
  calibrationProfile?: CalibrationProfile | null
}

export function SharedAidStationsView({ aidStations, segments, calibrationProfile }: SharedAidStationsViewProps) {
  if (!aidStations?.length) {
    return (
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal p-8">
        <h3 className="text-2xl font-bold text-primary-900 mb-4">Aid Stations</h3>
        <p className="text-neutral-600">No aid stations configured for this run.</p>
      </div>
    )
  }

  // Use centralized timeline to calculate proper ETAs including all stop times
  const timeline = calculateSegmentTimeline(segments, aidStations, calibrationProfile || undefined)
  const aidStationData = timeline.aidStations.sort((a, b) => a.station.at_distance_km - b.station.at_distance_km)

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal">
      <div className="p-8">
        <h3 className="text-2xl font-bold text-primary-900 mb-2">Aid Stations</h3>
        <p className="text-sm text-neutral-600 mb-6">
          {aidStations.length} aid stations along the course
        </p>
      </div>
      <div className="border-t border-neutral-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Station</TableHead>
              <TableHead className="text-right">Distance</TableHead>
              <TableHead className="text-right">Arrival Time</TableHead>
              <TableHead className="text-right">Stop Duration</TableHead>
              <TableHead className="text-right">Departure Time</TableHead>
              <TableHead>Available Supplies</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {aidStationData.map((stationData) => {
              const station = stationData.station
              const stopMinutes = stationData.stopTime

              return (
                <TableRow key={station.id}>
                  <TableCell>
                    <div className="font-medium">{station.name}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    {station.at_distance_km.toFixed(1)} km
                  </TableCell>
                  <TableCell className="text-right">
                    {formatTime(stationData.arrivalTime)}
                  </TableCell>
                  <TableCell className="text-right">
                    {stopMinutes > 0 ? formatTime(stopMinutes) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {stopMinutes > 0 ? formatTime(stationData.departureTime) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {Array.isArray(station.available) && station.available.length > 0 ? (
                        station.available.map((item, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {String(item)}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">None specified</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {station.notes ? (
                      <p className="text-sm text-gray-600">{station.notes}</p>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}